import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface Testimonial {
  id: string;
  name: string;
  text: string;
  rating: number;
  date: string;
  service: string;
}

export interface BookingPageConfig {
  id: string;
  slug: string;
  is_active: boolean;
  headline: string | null;
  description: string | null;
  cover_photo_url: string | null;
  font_mood: string;
  social_links: Record<string, string>;
  show_prices: boolean;
  show_operators: boolean;
  operator_selection_mode: string;
  cancellation_policy: string | null;
  kiosk_enabled: boolean;
  kiosk_greeting: string | null;
  kiosk_auto_reset_seconds: number;
  allow_walk_ins: boolean;
  slot_interval_minutes: number;
  testimonials: Testimonial[];
  review_summary: Record<string, unknown>;
  show_testimonials: boolean;
}

function slugify(name: string): string {
  return name.toLowerCase().replace(/'/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function useBookingPageSettings(businessId: string | undefined, businessName?: string) {
  const [config, setConfig] = useState<BookingPageConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNew, setIsNew] = useState(false);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('booking_pages')
        .select('id, slug, is_active, headline, description, cover_photo_url, font_mood, social_links, show_prices, show_operators, operator_selection_mode, cancellation_policy, kiosk_enabled, kiosk_greeting, kiosk_auto_reset_seconds, allow_walk_ins, slot_interval_minutes, testimonials, review_summary, show_testimonials')
        .eq('business_id', businessId)
        .maybeSingle();

      if (err) throw err;

      if (data) {
        if (mountedRef.current) { setConfig(data as BookingPageConfig); setIsNew(false); }
      } else {
        // Auto-create with generated slug
        let slug = slugify(businessName ?? 'my-business');
        // Check uniqueness
        const { data: existing } = await supabase.from('booking_pages').select('id').eq('slug', slug).maybeSingle();
        if (existing) slug = `${slug}-${Math.floor(Math.random() * 900) + 100}`;

        const { data: created, error: createErr } = await supabase
          .from('booking_pages')
          .insert({
            business_id: businessId, slug, is_active: true,
            font_mood: 'clean', show_prices: true, show_operators: false,
            operator_selection_mode: 'optional', kiosk_enabled: false,
            allow_walk_ins: true, kiosk_auto_reset_seconds: 15,
            slot_interval_minutes: 15, social_links: {},
          })
          .select('id, slug, is_active, headline, description, cover_photo_url, font_mood, social_links, show_prices, show_operators, operator_selection_mode, cancellation_policy, kiosk_enabled, kiosk_greeting, kiosk_auto_reset_seconds, allow_walk_ins, slot_interval_minutes, testimonials, review_summary, show_testimonials')
          .maybeSingle();

        if (!createErr && created && mountedRef.current) { setConfig(created as BookingPageConfig); setIsNew(true); }
      }
    } catch {
      // silent
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [businessId, businessName]);

  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  const updateConfig = useCallback(async (updates: Partial<BookingPageConfig>): Promise<boolean> => {
    if (!businessId || !config) return false;
    try {
      const { error: err } = await supabase
        .from('booking_pages').update(updates).eq('business_id', businessId);
      if (err) return false;
      // Mark the tenant as having customized their booking page — what
      // get_setup_progress.has_booking_page reads. The initial auto-create
      // in fetch() above is a seed and must NOT flip this; only explicit
      // user-initiated updates count. Migration 132 (logical "Migration 128").
      const { error: touchErr } = await supabase
        .from('businesses')
        .update({ booking_page_customized_at: new Date().toISOString() })
        .eq('id', businessId);
      if (touchErr) return false;
      setConfig(prev => prev ? { ...prev, ...updates } : prev);
      return true;
    } catch { return false; }
  }, [businessId, config]);

  const uploadCoverPhoto = useCallback(async (file: File): Promise<string | null> => {
    if (!businessId) return null;
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${businessId}/cover.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from('booking-assets').upload(path, file, { upsert: true });
      if (uploadErr) {
        // Bucket may not exist — try invoice-logos as fallback
        const fallbackPath = `${businessId}/booking-cover.${ext}`;
        const { error: fallbackErr } = await supabase.storage
          .from('invoice-logos').upload(fallbackPath, file, { upsert: true });
        if (fallbackErr) return null;
        const { data: urlData } = supabase.storage.from('invoice-logos').getPublicUrl(fallbackPath);
        const url = urlData.publicUrl;
        await updateConfig({ cover_photo_url: url });
        return url;
      }
      const { data: urlData } = supabase.storage.from('booking-assets').getPublicUrl(path);
      const url = urlData.publicUrl;
      await updateConfig({ cover_photo_url: url });
      return url;
    } catch { return null; }
  }, [businessId, updateConfig]);

  return { config, loading, isNew, updateConfig, uploadCoverPhoto, refresh: fetch };
}
